import React, { useEffect, useRef, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Youtube, Check, X, Edit3, ClipboardPaste, Hand } from "lucide-react";

/**
 * Admin-only widget to swap an exercise's looped gif demo for an embedded
 * YouTube video. Two paths cover every browser:
 *
 *   1. CLIPBOARD API — fast one-tap "Paste & save" via
 *      `navigator.clipboard.readText()`. Works on Chrome, Safari, Edge,
 *      Firefox with permission granted.
 *   2. NATIVE PASTE EVENT (fallback) — autofocused input with onPaste
 *      capture. The user long-presses → Paste from the OS menu and we
 *      submit immediately. This path works in EVERY browser including
 *      Brave (whose Shields block the Clipboard API by default), because
 *      no JS clipboard access is required — the browser fires the paste
 *      event natively in response to the user gesture.
 *
 * Cross-env note: the override is stored in this environment's MongoDB.
 * Embeds set in preview do NOT auto-sync to production — re-paste after
 * deploy, or bake the finalised set into seed.py.
 */
export default function AdminDemoOverride({ exercise, onUpdate }) {
    const [editing, setEditing] = useState(false);
    const [url, setUrl] = useState("");
    const [saving, setSaving] = useState(false);
    const inputRef = useRef(null);

    const currentYt = exercise?.youtube_id || "";

    // When the manual editing view opens, focus the input so a single long-press
    // on it triggers the OS native paste menu without an extra tap.
    useEffect(() => {
        if (editing && inputRef.current) {
            inputRef.current.focus();
        }
    }, [editing]);

    const submit = async (rawUrl) => {
        const finalUrl = (rawUrl || "").trim();
        if (!finalUrl) {
            toast.message("Paste a YouTube URL first.");
            return false;
        }
        setSaving(true);
        try {
            const { data } = await api.patch(`/admin/exercises/${exercise.id}/demo`, {
                youtube_url: finalUrl,
            });
            toast.success(data.youtube_id ? `Demo set — video ${data.youtube_id}` : "Demo cleared");
            setEditing(false);
            setUrl("");
            onUpdate?.();
            return true;
        } catch (e) {
            const msg = e?.response?.data?.detail || "Could not save";
            toast.error(msg);
            return false;
        } finally {
            setSaving(false);
        }
    };

    /** One-tap path:
     *  1. Try the Clipboard API. If it works, submit immediately.
     *  2. If the browser blocks it (Brave Shields, private mode, no HTTPS),
     *     fall through to `window.prompt()` — a NATIVE system dialog that
     *     bypasses every web-level clipboard restriction because the OS owns
     *     the input field. iOS / Android long-press always shows Paste here.
     *  3. If the user cancels the prompt, we silently bail (no error toast).
     */
    const tryClipboardThenFallback = async () => {
        try {
            if (navigator.clipboard && navigator.clipboard.readText) {
                const text = (await navigator.clipboard.readText() || "").trim();
                if (text) {
                    await submit(text);
                    return;
                }
            }
        } catch (e) {
            // intentional — fall through to system prompt
        }
        // Native system prompt: works in every browser including Brave,
        // because the input is a system control, not a web element.
        // eslint-disable-next-line no-alert
        const pasted = window.prompt(
            "Paste your YouTube URL or video ID here:\n(Long-press → Paste in the system dialog)"
        );
        if (pasted && pasted.trim()) {
            await submit(pasted);
        }
    };

    const handlePaste = async (e) => {
        // Native paste event fires on long-press → Paste in every browser
        // (Brave, Safari, Chrome, Firefox) — no JS clipboard permission
        // required. We swallow the default and submit immediately.
        const text = (e.clipboardData?.getData("text") || "").trim();
        if (!text) return;
        e.preventDefault();
        setUrl(text);
        await submit(text);
    };

    const clear = async () => {
        setSaving(true);
        try {
            await api.patch(`/admin/exercises/${exercise.id}/demo`, { youtube_url: "" });
            toast.success("Demo cleared — looped gif restored");
            setEditing(false);
            setUrl("");
            onUpdate?.();
        } catch (e) {
            toast.error("Could not clear");
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="tactical-card p-4" data-testid="admin-demo-override">
            <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-2">
                    <Youtube className="h-4 w-4 text-accent-red" />
                    <div className="label-eyebrow text-accent-red">Admin · demo override</div>
                </div>
                {currentYt && !editing && (
                    <span className="rounded-sm border border-emerald-700 bg-emerald-500/10 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-emerald-400" data-testid="admin-demo-current">
                        YouTube · {currentYt}
                    </span>
                )}
                {!currentYt && !editing && (
                    <span className="text-[10px] uppercase tracking-wider text-muted-foreground" data-testid="admin-demo-fallback">
                        Looped gif fallback
                    </span>
                )}
            </div>

            {!editing && (
                <div className="mt-3 flex flex-wrap items-center gap-2">
                    <button
                        type="button"
                        onClick={tryClipboardThenFallback}
                        disabled={saving}
                        data-testid="admin-demo-paste-save"
                        className="inline-flex items-center gap-1.5 rounded-sm bg-accent-red px-3 py-1.5 text-xs font-bold uppercase tracking-wider text-white hover:bg-[color:var(--accent-hover)] disabled:opacity-50"
                    >
                        <ClipboardPaste className="h-3 w-3" />
                        Paste YouTube link
                    </button>
                    {currentYt && (
                        <button
                            type="button"
                            onClick={clear}
                            disabled={saving}
                            data-testid="admin-demo-clear-collapsed"
                            className="inline-flex items-center gap-1 rounded-sm border border-border px-3 py-1.5 text-xs font-bold uppercase tracking-wider hover:bg-secondary disabled:opacity-50"
                        >
                            <X className="h-3 w-3" /> Clear
                        </button>
                    )}
                    <button
                        type="button"
                        onClick={() => { setEditing(true); setUrl(""); }}
                        data-testid="admin-demo-edit"
                        className="inline-flex items-center gap-1.5 rounded-sm border border-border bg-secondary/30 px-3 py-1.5 text-xs font-bold uppercase tracking-wider hover:bg-secondary"
                    >
                        <Edit3 className="h-3 w-3" /> Edit manually
                    </button>
                </div>
            )}

            {editing && (
                <div className="mt-3 space-y-2">
                    <div className="rounded-sm border border-accent-red/40 bg-accent-red/5 p-3">
                        <div className="flex items-center gap-1.5 label-eyebrow text-accent-red">
                            <Hand className="h-3 w-3" /> Long-press the field, then tap Paste
                        </div>
                        <p className="mt-1 text-[11px] leading-snug text-muted-foreground">
                            Brave & some private-mode browsers block one-tap clipboard reads. Long-press the field below, choose <b>Paste</b> from the OS menu, and we'll save automatically the instant you do.
                        </p>
                    </div>
                    <input
                        ref={inputRef}
                        type="url"
                        inputMode="url"
                        placeholder="Long-press here → Paste"
                        value={url}
                        onChange={(e) => setUrl(e.target.value)}
                        onPaste={handlePaste}
                        data-testid="admin-demo-input"
                        className="w-full rounded-sm border-2 border-accent-red/60 bg-background px-3 py-3 text-base"
                    />
                    <p className="text-[10px] uppercase tracking-wider text-muted-foreground">
                        Accepts watch · short · embed URLs or a bare 11-char video ID.
                    </p>
                    <div className="flex flex-wrap items-center gap-2">
                        <button
                            type="button"
                            onClick={() => submit(url)}
                            disabled={saving || !url.trim()}
                            data-testid="admin-demo-save"
                            className="inline-flex items-center gap-1 rounded-sm bg-accent-red px-3 py-1.5 text-xs font-bold uppercase tracking-wider text-white hover:bg-[color:var(--accent-hover)] disabled:opacity-50"
                        >
                            <Check className="h-3 w-3" /> Save
                        </button>
                        {currentYt && (
                            <button
                                type="button"
                                onClick={clear}
                                disabled={saving}
                                data-testid="admin-demo-clear"
                                className="inline-flex items-center gap-1 rounded-sm border border-border px-3 py-1.5 text-xs font-bold uppercase tracking-wider hover:bg-secondary disabled:opacity-50"
                            >
                                <X className="h-3 w-3" /> Clear override
                            </button>
                        )}
                        <button
                            type="button"
                            onClick={() => { setEditing(false); setUrl(""); }}
                            disabled={saving}
                            data-testid="admin-demo-cancel"
                            className="text-xs uppercase tracking-wider text-muted-foreground hover:text-foreground disabled:opacity-50"
                        >
                            cancel
                        </button>
                    </div>
                </div>
            )}

            <p className="mt-3 text-[10px] leading-snug text-muted-foreground">
                Note: this override is stored in this environment's database only. It does <b>not</b> sync to production automatically — re-paste after deploying, or bake into <code>seed.py</code>.
            </p>
        </div>
    );
}
